import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { formatSavedTime } from '../utils/formatSavedTime'
import { formatDate } from 'lib/utils'

export interface TimeSavedChartPoint {
  date: string
  formattedDate: string
  minutes: number
  interviewMinutes: number
  totalCandidateCreation: number
  candidateCreationInvalid: number
  totalOfferCreation: number
  totalOfferModification: number
}

export interface InterviewTimeSavedHistoryPoint {
  date: string
  interview_time_saved_minutes: number
}

interface TimeSavedChartProps {
  data: Omit<TimeSavedChartPoint, 'interviewMinutes'>[]
  interviewHistory?: InterviewTimeSavedHistoryPoint[]
}

export function TimeSavedChart({ data, interviewHistory = [] }: TimeSavedChartProps) {
  const { t } = useTranslation()

  const chartData = useMemo(() => {
    const byDate = new Map<string, TimeSavedChartPoint>()

    for (const item of data) {
      byDate.set(item.date, {
        ...item,
        interviewMinutes: 0,
      })
    }

    for (const item of interviewHistory) {
      const existing = byDate.get(item.date)
      const interviewMinutes = Number(item.interview_time_saved_minutes || 0)

      if (existing) {
        existing.interviewMinutes = interviewMinutes
      } else {
        byDate.set(item.date, {
          date: item.date,
          formattedDate: formatDate(item.date),
          minutes: 0,
          interviewMinutes,
          totalCandidateCreation: 0,
          candidateCreationInvalid: 0,
          totalOfferCreation: 0,
          totalOfferModification: 0,
        })
      }
    }

    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date))
  }, [data, interviewHistory])

  const dataAvailability = useMemo(() => ({
    hasCandidateCreation: chartData.some(item => item.totalCandidateCreation > 0),
    hasMatching: chartData.some(item => item.minutes > 0),
    hasInterview: chartData.some(item => item.interviewMinutes > 0),
  }), [chartData])

  const matchingLabel = t('dashboard.recruitmentStats.timeSavedTitle')
  const interviewLabel = t('dashboard.recruitmentStats.interviewTimeSavedTitle')

  return (
    <Card className="flex h-full w-full max-w-full flex-col rounded-[6px]">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold tracking-normal text-foreground">
          {t('dashboard.recruitmentStats.timeSavedChartTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col">
        <div className="h-full min-h-[380px] w-full flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="dashboardMatchingTimeSaved" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.28} />
                  <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="dashboardInterviewTimeSaved" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.28} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#D4D4D4" />
              <XAxis
                dataKey="formattedDate"
                tickLine={false}
                axisLine={{ stroke: '#8A8A8A' }}
                tick={{ fill: '#666666', fontSize: 13 }}
              />
              <YAxis
                width={72}
                tickLine={false}
                axisLine={{ stroke: '#8A8A8A' }}
                tick={{ fill: '#666666', fontSize: 13 }}
                tickFormatter={(value) => `${Number(value).toFixed(0)} min`}
              />
              <Legend
                verticalAlign="top"
                height={28}
                formatter={(value) => (
                  <span className="text-xs text-muted-foreground">{value}</span>
                )}
              />
              <Tooltip
                cursor={{ stroke: '#BDBDBD', strokeWidth: 1 }}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload[0]) return null
                  const point = payload[0].payload as TimeSavedChartPoint

                  return (
                    <div className="rounded-[8px] border border-border bg-card px-4 py-3 text-card-foreground shadow-lg">
                      <p className="mb-2 text-sm font-semibold">{point.formattedDate}</p>
                      <div className="space-y-1 text-xs">
                        {dataAvailability.hasMatching && (
                          <p className="font-semibold text-[#0EA5E9]">
                            {matchingLabel}: {formatSavedTime(point.minutes)}
                          </p>
                        )}
                        {(dataAvailability.hasInterview || point.interviewMinutes > 0) && (
                          <p className="font-semibold text-[#8B5CF6]">
                            {interviewLabel}: {formatSavedTime(point.interviewMinutes)}
                          </p>
                        )}
                        {dataAvailability.hasCandidateCreation && (
                          <div className="mt-2 border-t border-border pt-2">
                            <p className="text-[#0284C7]">
                              {t('dashboard.recruitmentStats.evaluatedCvs', 'CV évalués')}: {point.totalCandidateCreation}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }}
              />
              <Area
                type="monotone"
                dataKey="minutes"
                name={matchingLabel}
                stroke="#0EA5E9"
                strokeWidth={2.5}
                fill="url(#dashboardMatchingTimeSaved)"
                activeDot={{ r: 5, fill: '#0EA5E9', stroke: '#FFFFFF', strokeWidth: 2 }}
              />
              <Area
                type="monotone"
                dataKey="interviewMinutes"
                name={interviewLabel}
                stroke="#8B5CF6"
                strokeWidth={2.5}
                fill="url(#dashboardInterviewTimeSaved)"
                activeDot={{ r: 5, fill: '#8B5CF6', stroke: '#FFFFFF', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
